import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react'
import type { Idea } from '../api'
import { Link } from '../app/Link'
import { navigate } from '../app/routing'
import { useWide } from '../app/viewport'
import { GripIcon, PlayIcon } from '../components/icons'
import { IdeaMeta, VideoRings, VideoThumb } from '../components/IdeaBits'
import { PipelineHead } from '../components/PipelineHead'
import { useSeriesView } from '../components/SeriesFilter'
import { byStage, isQuickToFilm, STAGE_LABEL, STAGES } from '../domain/stages'
import { isPending, loadIdeas, useIdeas } from '../state/ideas'
import { setQueue, useQueue } from '../state/queue'
import { stageViewStore, useStageView } from '../state/stageView'
import { Canvas } from './Canvas'
import { IdeaPanel } from './IdeaDetail'
import './pipeline.css'

/**
 * Home: every idea being made, in the four stages the work runs through.
 *
 * On a laptop, a canvas with a frame per stage, and an idea opens in a panel
 * beside it. On the phone, one stage at a time: a switch across the top, the
 * stage's list under it, and an idea opens as a page. The stages are the same
 * in both, so moving between the phone and the laptop moves nothing.
 */
export function Pipeline({ openId }: { openId?: number }) {
  const wide = useWide()
  if (!wide) return <StageList />
  return (
    <div className="desk">
      <Canvas selected={openId ?? null} />
      {openId !== undefined && <IdeaPanel key={openId} id={openId} />}
    </div>
  )
}

function StageList() {
  const { ideas, phase, error } = useIdeas()
  const { ids, batch } = useQueue()
  const series = useSeriesView()
  const { stage, flash } = useStageView()
  const stages = useMemo(() => byStage(ideas, ids), [ideas, ids])
  const inPipeline = useMemo(() => STAGES.flatMap((s) => stages[s]), [stages])
  const shown = (list: Idea[]) => (series === 'all' ? list : list.filter((idea) => idea.series === series))
  const rows = shown(stages[stage])

  // The row just come back from is lit for a moment, and scrolled to.
  useEffect(() => {
    if (flash === null) return
    document.querySelector(`[data-row="${flash}"]`)?.scrollIntoView({ block: 'center' })
    const timer = setTimeout(() => stageViewStore.set((view) => ({ ...view, flash: null })), 1600)
    return () => clearTimeout(timer)
  }, [flash])

  return (
    <main className="screen stages">
      <PipelineHead ideas={inPipeline} />
      <nav className="stage-switch" aria-label="Stage">
        {STAGES.map((value) => (
          <button
            key={value}
            type="button"
            aria-pressed={stage === value}
            onClick={() => stageViewStore.set({ stage: value, flash: null })}
          >
            {STAGE_LABEL[value]}
            <span className="stage-count mono">{shown(stages[value]).length}</span>
          </button>
        ))}
      </nav>

      {phase === 'loading' && <Skeleton />}
      {phase === 'error' && (
        <div className="empty">
          <p>{error ?? 'Could not load ideas.'}</p>
          <button type="button" className="button" onClick={() => void loadIdeas()}>
            Try again
          </button>
        </div>
      )}

      {phase === 'ready' && stage === 'ideas' && <IdeasList ideas={rows} flash={flash} />}
      {phase === 'ready' && stage === 'scripted' && (
        <ul className="stage-list">
          {rows.map((idea) => (
            <Row key={idea.id} idea={idea} flash={flash} />
          ))}
          {!rows.length && <li className="stage-empty">Nothing scripted. Open an idea and write the lines.</li>}
        </ul>
      )}
      {phase === 'ready' && stage === 'filming' && <FilmingList ideas={rows} queue={ids} flash={flash} />}
      {phase === 'ready' && stage === 'post' && <PostList ideas={rows} flash={flash} />}

      {stage === 'filming' && ids.length > 0 && (
        <div className="start-filming">
          <button
            type="button"
            className="button button-primary"
            disabled={!batch}
            onClick={() => batch && navigate(`/film/${batch.id}`)}
          >
            <PlayIcon size={15} />
            Start filming
            <span className="start-filming-count mono">{ids.length}</span>
          </button>
        </div>
      )}
    </main>
  )
}

/** Questions and terms first: they need no script and go straight to the camera. */
function IdeasList({ ideas, flash }: { ideas: Idea[]; flash: number | null }) {
  if (!ideas.length) return <p className="stage-empty">No ideas here. Add one with +.</p>
  const groups = [
    { label: 'Quick to film', ideas: ideas.filter(isQuickToFilm) },
    { label: 'Needs a script', ideas: ideas.filter((idea) => !isQuickToFilm(idea)) },
  ].filter((group) => group.ideas.length)
  return (
    <>
      {groups.map((group) => (
        <section key={group.label} className="stage-group" aria-label={group.label}>
          <h2 className="stage-group-title">
            {group.label}
            <span className="stage-count mono">{group.ideas.length}</span>
          </h2>
          <ul className="stage-list">
            {group.ideas.map((idea) => (
              <Row key={idea.id} idea={idea} flash={flash} />
            ))}
          </ul>
        </section>
      ))}
    </>
  )
}

function Row({ idea, flash, lead, trail }: { idea: Idea; flash: number | null; lead?: ReactNode; trail?: ReactNode }) {
  const body = (
    <>
      {lead}
      <span className="stage-row-text">
        <span className="stage-row-title">{idea.title}</span>
        <IdeaMeta idea={idea} />
      </span>
    </>
  )
  return (
    <li className="stage-row" data-row={idea.id} data-flash={flash === idea.id || undefined} data-pending={isPending(idea) || undefined}>
      {isPending(idea) ? (
        <span className="stage-row-main">{body}</span>
      ) : (
        <Link to={`/idea/${idea.id}`} className="stage-row-main">
          {body}
        </Link>
      )}
      {trail}
    </li>
  )
}

/**
 * The next batch, in the order it will be read. The handle on the right
 * drags a row up or down; the order is saved when it is let go.
 */
function FilmingList({ ideas, queue, flash }: { ideas: Idea[]; queue: number[]; flash: number | null }) {
  const [order, setOrder] = useState<number[] | null>(null)
  const dragging = useRef<number | null>(null)
  const list = useRef<HTMLUListElement>(null)

  if (!ideas.length) {
    return <p className="stage-empty">No batch yet. Open an idea and move it to Filming to queue it for the next time you film.</p>
  }
  const current = order ?? queue
  const rows = [...ideas].sort((a, b) => current.indexOf(a.id) - current.indexOf(b.id))

  const start = (id: number) => (event: ReactPointerEvent) => {
    event.preventDefault()
    event.currentTarget.setPointerCapture(event.pointerId)
    dragging.current = id
    setOrder(queue)
  }
  const move = (event: ReactPointerEvent) => {
    const id = dragging.current
    if (id === null || !order) return
    for (const row of list.current?.querySelectorAll<HTMLElement>('[data-row]') ?? []) {
      const over = Number(row.dataset.row)
      const box = row.getBoundingClientRect()
      if (over !== id && event.clientY > box.top && event.clientY < box.bottom) {
        const without = order.filter((item) => item !== id)
        without.splice(order.indexOf(over), 0, id)
        setOrder(without)
        break
      }
    }
  }
  const end = () => {
    if (order && order.join() !== queue.join()) void setQueue(order)
    dragging.current = null
    setOrder(null)
  }

  return (
    <>
      <p className="batch-lede">
        {ideas.length} {ideas.length === 1 ? 'card' : 'cards'}, in the order you read them.
      </p>
      <ul className="stage-list" ref={list}>
        {rows.map((idea) => (
          <Row
            key={idea.id}
            idea={idea}
            flash={flash}
            lead={<span className="batch-number mono">{current.indexOf(idea.id) + 1}</span>}
            trail={
              <button
                type="button"
                className="batch-grip"
                aria-label={`Drag to reorder ${idea.title}`}
                data-dragging={dragging.current === idea.id || undefined}
                onPointerDown={start(idea.id)}
                onPointerMove={move}
                onPointerUp={end}
                onPointerCancel={end}
              >
                <GripIcon size={18} />
              </button>
            }
          />
        ))}
      </ul>
    </>
  )
}

/** Ready to post first, then what is still in the edit. */
function PostList({ ideas, flash }: { ideas: Idea[]; flash: number | null }) {
  if (!ideas.length) return <p className="stage-empty">Nothing waiting. Finished videos land here from the edit.</p>
  const groups = [
    { label: 'Ready to post', ideas: ideas.filter((idea) => idea.status === 'edited') },
    { label: 'In the edit', ideas: ideas.filter((idea) => idea.status === 'filmed') },
  ].filter((group) => group.ideas.length)
  return (
    <>
      {groups.map((group) => (
        <section key={group.label} className="stage-group" aria-label={group.label}>
          <h2 className="stage-group-title">
            {group.label}
            <span className="stage-count mono">{group.ideas.length}</span>
          </h2>
          <ul className="stage-list">
            {group.ideas.map((idea) => (
              <Row
                key={idea.id}
                idea={idea}
                flash={flash}
                lead={<VideoThumb idea={idea} />}
                trail={idea.status === 'edited' ? <VideoRings idea={idea} /> : undefined}
              />
            ))}
          </ul>
        </section>
      ))}
    </>
  )
}

function Skeleton() {
  return (
    <ul className="stage-list skeleton" aria-label="Loading">
      {[72, 54, 86, 40, 66, 58].map((width, index) => (
        <li key={index} className="stage-row">
          <span className="stage-row-main">
            <span className="skeleton-bar" style={{ width: `${width}%` }} />
          </span>
        </li>
      ))}
    </ul>
  )
}
